/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Datadog", description = "The Datadog extension allows you to receive real-time metrics and events about your syncs", lastUpdatedDate = new Date(1693180800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Datadog",
        description: "The Datadog extension allows you to receive real-time metrics and events about your syncs",
        lastUpdatedDate: "Mon Aug 28 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Emitted metrics and events",
          "depth": 0,
          "id": "emitted-metrics-and-events"
        }, {
          "value": "Metrics",
          "depth": 1,
          "id": "metrics"
        }, {
          "value": "Events",
          "depth": 1,
          "id": "events"
        }, {
          "value": "Use cases",
          "depth": 0,
          "id": "use-cases"
        }, {
          "value": "Completed sync status",
          "depth": 1,
          "id": "completed-sync-status"
        }, {
          "value": "Anomaly detection",
          "depth": 1,
          "id": "anomaly-detection"
        }, {
          "value": "Other use cases",
          "depth": 1,
          "id": "other-use-cases"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      code: "code",
      strong: "strong",
      ol: "ol",
      li: "li",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      img: "img"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch integrates with Datadog to help you gain greater observability into your syncs and monitor their health. Once you've authorized access to Datadog in Hightouch, we send real-time sync metrics and events to your Datadog account. You can then aggregate these statistics to gain insight into the health of your syncs."
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["You need your ", _jsx(_components.a, {
          href: "https://docs.datadoghq.com/getting_started/site/",
          children: "Datadog site"
        }), " and ", _jsx(_components.a, {
          href: "https://docs.datadoghq.com/account_management/api-app-keys/",
          children: "API key"
        }), " to set up the extension. You may want to create a new API key for Hightouch. Make sure that the API key creator in Datadog has the necessary write permissions for ", _jsx(_components.a, {
          href: "https://docs.datadoghq.com/metrics/custom_metrics/",
          children: "custom metrics"
        }), " (", _jsx(_components.code, {
          children: "Metric Tags Write"
        }), ", ", _jsx(_components.code, {
          children: "Host Tags Write"
        }), ", ", _jsx(_components.code, {
          children: "Metrics Metadata Write"
        }), ") and ", _jsx(_components.a, {
          href: "https://docs.datadoghq.com/events/",
          children: "events"
        }), " (", _jsx(_components.code, {
          children: "Event Config Write"
        }), ")."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Make sure to use the ", _jsx(_components.strong, {
            children: "API Key"
          }), " itself and not the Key ID. Also ensure you use an API Key and not an Application Key to avoid the ", _jsx(_components.code, {
            children: "Datadog configuration is invalid"
          }), " error. You can find more information about the difference between these keys in ", _jsx(_components.a, {
            href: "https://docs.datadoghq.com/account_management/api-app-keys/",
            children: "Datadog's documentation"
          }), "."]
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/extensions/monitoring?from=docs",
            children: [_jsx(_components.strong, {
              children: "Monitoring"
            }), " section"]
          }), " of the ", _jsx(_components.strong, {
            children: "Extensions"
          }), " page and select ", _jsx(_components.strong, {
            children: "Configure extension"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select your Datadog ", _jsx(_components.a, {
            href: "https://docs.datadoghq.com/getting_started/site/",
            children: _jsx(_components.strong, {
              children: "site"
            })
          }), " and enter your ", _jsx(_components.strong, {
            children: "API Key"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Once you've saved your Datadog credentials in Hightouch, your syncs automatically start emitting metrics and events to Datadog."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch metrics and events don't immediately show up in Datadog, but only once sync runs are triggered."
        })
      }), "\n", _jsx(_components.h2, {
        id: "emitted-metrics-and-events",
        children: "Emitted metrics and events"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch emits both ", _jsx(_components.a, {
          href: "https://docs.datadoghq.com/metrics/types/",
          children: "metrics"
        }), " and ", _jsx(_components.a, {
          href: "https://docs.datadoghq.com/events/",
          children: "events"
        }), ". Metrics are best suited for dashboards and statistical aggregations, while events are best for filtering. Don't hesitate to ", _jsx(PylonLink, {
          pylonText: "Hello, I have a feature request. I'm interested in a new metric or event in Datadog:",
          linkText: "get in touch"
        }), " if there's another metric or event you're interested in using."]
      }), "\n", _jsx(_components.h3, {
        id: "metrics",
        children: "Metrics"
      }), "\n", _jsx(_components.h4, {
        id: "hightouchsyncrow_processed",
        children: _jsx(_components.code, {
          children: "hightouch.sync.row_processed"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This metric is a ", _jsx(_components.a, {
          href: "https://docs.datadoghq.com/metrics/types/?tab=count",
          children: _jsx(_components.code, {
            children: "COUNT"
          })
        }), " type metric and is incremented for each row processed in the sync."]
      }), "\n", _jsx(_components.p, {
        children: "It provides the following tags:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "sync"
          }), " - The sync slug in Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "workspace"
          }), " - The slug of the Hightouch workspace that contains the sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "op"
          }), " (added/changed/removed) - The operation performed: whether the row was added, changed, or removed in the query."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "success"
          }), " (true/false) - Whether the row was successfully synced to the destination."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "hightouchsynccomplete",
        children: _jsx(_components.code, {
          children: "hightouch.sync.complete"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This metric is a ", _jsx(_components.a, {
          href: "https://docs.datadoghq.com/metrics/types/?tab=count",
          children: _jsx(_components.code, {
            children: "COUNT"
          })
        }), " type metric and is incremented each time the sync completes."]
      }), "\n", _jsx(_components.p, {
        children: "It supports the following tags:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "sync"
          }), " - The sync slug in Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "workspace"
          }), " - The slug of the Hightouch workspace that contains the sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "status"
          }), " (success/failed/warning/cancelled) - The overall status of the sync run."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "hightouchsyncquery_time",
        children: _jsx(_components.code, {
          children: "hightouch.sync.query_time"
        })
      }), "\n", _jsx(_components.p, {
        children: "This metric tracks your syncs' querying times."
      }), "\n", _jsx(_components.p, {
        children: "It supports the following tags:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "sync"
          }), " - The sync slug in Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "workspace"
          }), " - The slug of the Hightouch workspace that contains the sync."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "hightouchsyncexecution_time",
        children: _jsx(_components.code, {
          children: "hightouch.sync.execution_time"
        })
      }), "\n", _jsx(_components.p, {
        children: "This metric tracks your syncs' execution times."
      }), "\n", _jsx(_components.p, {
        children: "It supports the following tags:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "sync"
          }), " - The sync slug in Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "workspace"
          }), " - The slug of the Hightouch workspace that contains the sync."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "hightouchsynctotal_time",
        children: _jsx(_components.code, {
          children: "hightouch.sync.total_time"
        })
      }), "\n", _jsx(_components.p, {
        children: "This metric tracks your syncs' total run times."
      }), "\n", _jsx(_components.p, {
        children: "It supports the following tags:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "sync"
          }), " - The sync slug in Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "workspace"
          }), " - The slug of the Hightouch workspace that contains the sync."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "events",
        children: "Events"
      }), "\n", _jsx(_components.h4, {
        id: "hightouchsynccomplete-1",
        children: _jsx(_components.code, {
          children: "hightouch.sync.complete"
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch emits this event each time a sync completes. It's identical to the metric of the same name."
      }), "\n", _jsx(_components.p, {
        children: "It supports the following tags:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "sync"
          }), " - The sync slug in Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "workspace"
          }), " - The slug of the Hightouch workspace that contains the sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "status"
          }), " (success/failed/warning/canceled) - The overall status of the sync run."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "use-cases",
        children: "Use cases"
      }), "\n", _jsx(_components.p, {
        children: "With just a few metrics, you can build complex dashboards and monitors. For example, you can create dashboards that target specific syncs that are critical to your operations and report the total number of operations or top syncs that have warnings or failures."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/datadog-metrics-dashboard.png",
          alt: "Example Datadog dashboard"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can ", _jsx(_components.a, {
          href: "https://docs.datadoghq.com/dashboards/#copy-import-or-export-dashboard-json",
          children: "import the JSON"
        }), " for a basic dashboard using this ", _jsx(_components.a, {
          href: "https://gist.github.com/PedramNavid/71f73c98d98373053674f19b8239b06c",
          children: "sample code"
        }), " to get started."]
      }), "\n", _jsx(_components.h3, {
        id: "completed-sync-status",
        children: "Completed sync status"
      }), "\n", _jsx(_components.p, {
        children: "One common use case is to build a basic dashboard that tracks completed syncs by status over time."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/datadog-metrics-sync.png",
          alt: "Datadog sync dashboard"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The preceding chart uses the ", _jsxs(_components.a, {
          href: "#hightouchsynccomplete",
          children: [_jsx(_components.code, {
            children: "hightouch.sync.complete"
          }), " metric"]
        }), " and sums the counts by ", _jsx(_components.code, {
          children: "status"
        }), ". You can filter further by including only statuses that are ", _jsx(_components.code, {
          children: "status:success"
        }), " or ", _jsx(_components.code, {
          children: "NOT status:success"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "anomaly-detection",
        children: "Anomaly detection"
      }), "\n", _jsx(_components.p, {
        children: "Anomaly detection can help flag significant changes, like many users suddenly being deleted or created. You can use the Datadog extension for anomaly detection in added, changed, or removed rows."
      }), "\n", _jsx(_components.h4, {
        id: "anomaly-detection-setup",
        children: "Anomaly detection setup"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Datadog, create a ", _jsx(_components.a, {
            href: "https://docs.datadoghq.com/monitors/create/types/metric/",
            children: "metric monitor"
          }), " with the ", _jsx(_components.strong, {
            children: "Anomaly Detection"
          }), " method."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/datadog-anamoly-detection-method.png",
          alt: "Datadog metric monitor configuration"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Define the metric by selecting the ", _jsx(_components.a, {
            href: "##hightouchsyncrow_processed",
            children: _jsx(_components.code, {
              children: "hightouch.sync.row_processed"
            })
          }), " metric. Specify the ", _jsx(_components.code, {
            children: "op_type"
          }), ", for example, ", _jsx(_components.code, {
            children: "removed"
          }), ", and ", _jsx(_components.code, {
            children: "sync"
          }), " name. Select the metric aggregation as ", _jsx(_components.code, {
            children: "sum by"
          }), " and enter ", _jsx(_components.code, {
            children: "sync"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/datadog-sum-by-sync.png",
          alt: "Datadog metric monitor configuration with sum by sync"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.a, {
            href: "https://docs.datadoghq.com/monitors/create/types/metric/?tab=threshold#set-alert-conditions",
            children: _jsx(_components.strong, {
              children: "Alert Conditions"
            })
          }), ", set the ", _jsx(_components.strong, {
            children: "Alert threshold"
          }), " and ", _jsx(_components.strong, {
            children: "Warning threshold"
          }), " to values that make sense for your use case."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/datadog-alert-conditions.png",
          alt: "Datadog metric monitor configuration"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["(Optional) You can inject the sync name into the monitor's subject or message body using ", _jsx(_components.code, {
            children: "{{sync.name}}"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/datadog-notification.png",
          alt: "Datadog metric monitor configuration with sum by sync"
        })
      }), "\n", _jsx(_components.h3, {
        id: "other-use-cases",
        children: "Other use cases"
      }), "\n", _jsx(_components.p, {
        children: "You're not limited to the preceding examples. Here are some other use cases to consider:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Metric monitors for when the number of failed rows exceeds a threshold"
        }), "\n", _jsx(_components.li, {
          children: "Metric monitors for when a critical sync hasn't run over a specified time period"
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
